import type { ReactElement } from 'react';
import { IconButton } from '../ui/IconButton/IconButton';
import { useMediaQuery } from '../ui/hooks';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { MenuIcon, MoonIcon, SearchIcon, SidebarToggleIcon, SunIcon } from './icons';
import { DropdownMenu } from '../ui/DropdownMenu/DropdownMenu';
import { SIDEBAR_ID, SIDEBAR_MOBILE_QUERY } from './Sidebar';
import { UserMenu } from './UserMenu';
import { useTheme, type ThemeName } from './useTheme';
import styles from './TopBar.module.css';

export interface TopBarProps {
  /** Desktop sidebar rail state (owned by AppShell). */
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  /** Below the drawer breakpoint the same button opens the off-canvas nav instead. */
  onMenuClick: () => void;
  onSearchClick: () => void;
}

const THEME_LABEL: Record<ThemeName, string> = { light: 'Light', dark: 'Dark' };
const THEME_ICON: Record<ThemeName, ReactElement> = { light: <SunIcon />, dark: <MoonIcon /> };
const THEME_ITEMS: Array<{ value: ThemeName; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

function isMac(): boolean {
  try {
    return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  } catch {
    return false;
  }
}

/**
 * Compact command bar: sidebar toggle, global search (opens the command
 * palette — the search itself is the ticket list's server-side `q` filter),
 * notifications, theme, identity. No page title: each screen owns its h1 and
 * the brand lives in the sidebar. Deliberately not a toolbar of actions.
 */
export function TopBar({ sidebarCollapsed, onToggleSidebar, onMenuClick, onSearchClick }: TopBarProps) {
  const { effective: theme, setChoice: setTheme } = useTheme();
  const isMobile = useMediaQuery(SIDEBAR_MOBILE_QUERY);
  const shortcut = isMac() ? '⌘ K' : 'Ctrl + K';
  const themeLabel = `Theme: ${THEME_LABEL[theme]}`;

  return (
    <header className={styles.topbar}>
      <div className={styles.left}>
        {isMobile ? (
          <IconButton aria-label="Open navigation" className={styles.navButton} icon={<MenuIcon />} onClick={onMenuClick} />
        ) : (
          <IconButton
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
            aria-controls={SIDEBAR_ID}
            className={styles.navButton}
            icon={<SidebarToggleIcon />}
            onClick={onToggleSidebar}
          />
        )}
      </div>

      <div className={styles.center}>
        <button type="button" className={styles.search} onClick={onSearchClick} aria-keyshortcuts="Control+K Meta+K /">
          <SearchIcon className={styles.searchIcon} />
          <span className={styles.searchText}>Search tickets, users, or keywords...</span>
          <kbd className={styles.kbd} aria-hidden="true">
            {shortcut}
          </kbd>
          <span className="visually-hidden">(keyboard shortcut {shortcut})</span>
        </button>
      </div>

      <div className={styles.right}>
        <IconButton aria-label="Search" className={styles.searchIconButton} icon={<SearchIcon />} onClick={onSearchClick} />
        <div className={styles.bell}>
          <NotificationBell triggerClassName={styles.bellTrigger} badgeClassName={styles.bellBadge} />
        </div>
        {/* A menu of Light / Dark. The button shows the CURRENT theme's glyph;
            its accessible name states the current mode. */}
        <DropdownMenu
          aria-label="Choose theme"
          align="end"
          items={THEME_ITEMS.map((i) => ({ value: i.value, label: i.label, checked: theme === i.value }))}
          onSelect={(value) => setTheme(THEME_ITEMS.find((i) => i.value === value)?.value ?? null)}
          trigger={<IconButton aria-label={themeLabel} title={themeLabel} className={styles.themeButton} icon={THEME_ICON[theme]} />}
        />
        <UserMenu />
      </div>
    </header>
  );
}
